import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import type { StyleOptions } from '@primeuix/styled'

/* ============================================================
   Tivedo PrimeNG preset — corporate Nordic SaaS brand.
   Navy anchor + navy primary/CTA · cool slate neutrals · Inter.
   See docs/DESIGN-SYSTEM.md §8 (Mapping to PrimeNG).

   Primitive → semantic mapping:
     primary.50…950  ← var(--color-primary-50…950) theme tokens (FR-050)
     ink.* (slate)   ← var(--ink-*) legacy aliases (theme-aware)
   Components reference the §2.5 semantic CSS custom properties
   (--color-*, --rule, --radius-*, --shadow-*) so dark mode and any
   rebrand are a single token swap in styles.scss.
   ============================================================ */

/** PrimeNG primary palette steps; each maps to `--color-primary-<step>`. */
export const PRIMARY_RAMP_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const
export type PrimaryRampStep = (typeof PRIMARY_RAMP_STEPS)[number]

/** #3265 A2–A4: Aura gives every button severity its own `focusRing.shadow: none`,
 *  so a focused secondary/outlined/text/danger button showed no ring at all.
 *  Each one gets the `--shadow-focus` ring (§9: every interactive element). */
const BUTTON_SEVERITIES = ['primary', 'secondary', 'info', 'success', 'warn', 'help', 'danger', 'contrast'] as const
const BUTTON_FOCUS_RINGS = Object.fromEntries(
  BUTTON_SEVERITIES.map((sev) => [sev, { focusRing: { color: 'transparent', shadow: 'var(--shadow-focus)' } }])
)

/** #3265: the dropdowns take the text-input tokens (§7, the inputtext override
 *  below), so a select beside a pInputText field reads as the same control. Aura's
 *  defaults were a slate-300 border, 16px text, a 42px box, a black-tinted shadow. */
const DROPDOWN_FIELD_ROOT = {
  background: 'var(--color-bg-surface)',
  disabledBackground: 'var(--color-bg-subtle)',
  filledBackground: 'var(--color-bg-surface)',
  filledHoverBackground: 'var(--color-bg-surface)',
  filledFocusBackground: 'var(--color-bg-surface)',
  borderColor: 'var(--color-border)',
  hoverBorderColor: 'var(--color-border-strong)',
  focusBorderColor: 'var(--color-primary)',
  invalidBorderColor: 'var(--danger)',
  color: 'var(--color-text)',
  placeholderColor: 'var(--color-text-muted)',
  shadow: 'var(--shadow-xs)',
  borderRadius: 'var(--radius-md)',
}

export const TivedoPreset = definePreset(Aura, {
  semantic: {
    // Primary ramp (§2.2) — FR-050: every step reads a theme CSS variable so a
    // white-label brand re-colours checkboxes, radios, switches, paginator,
    // progress and selected rows at runtime (BrandService sets the variables on
    // <html>; no updatePreset call needed — PrimeNG emits `--p-primary-N:
    // var(--color-primary-N)`). Defaults live in styles.scss and are the
    // pre-FR-050 navy hex values (anchor 800 = #003764).
    primary: Object.fromEntries(
      PRIMARY_RAMP_STEPS.map((step) => [step, `var(--color-primary-${step})`])
    ) as Record<PrimaryRampStep, string>,
    // Cool slate neutrals — theme-aware via legacy ink aliases.
    ink: {
      300: 'var(--ink-300)',
      400: 'var(--ink-400)',
      500: 'var(--ink-500)',
      700: 'var(--ink-700)',
      900: 'var(--ink-900)',
      950: 'var(--ink-950)',
    },
    // 6px default radius (§4.2 radius-md).
    borderRadius: {
      none: '0',
      xs: '4px',
      sm: '4px',
      md: '6px',
      lg: '8px',
      xl: '12px',
    },
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: 'var(--shadow-focus)',
    },
    // #3265: Aura's form-field ring is `shadow: none`, so a focused select,
    // multiselect, textarea, autocomplete … only turned its border primary.
    // §7: input focus = border + shadow-focus, like the inputtext override below.
    formField: {
      focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'var(--shadow-focus)' },
    },
    colorScheme: {
      light: {
        primary: {
          // Default primary button = navy-800 (#003764) fill, white label (AA).
          color: '{primary.800}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.900}',
          activeColor: '{primary.950}',
        },
      },
      dark: {
        primary: {
          // Lighter navy on dark surfaces for contrast.
          color: '{primary.300}',
          contrastColor: 'var(--slate-900)',
          hoverColor: '{primary.200}',
          activeColor: '{primary.400}',
        },
      },
    },
  },
  components: {
    inputtext: {
      // Full bordered input (§7 inputs) — editorial border-bottom-only retired.
      root: {
        background: 'var(--color-bg-surface)',
        disabledBackground: 'var(--color-bg-subtle)',
        filledBackground: 'var(--color-bg-surface)',
        filledHoverBackground: 'var(--color-bg-surface)',
        filledFocusBackground: 'var(--color-bg-surface)',
        borderColor: 'var(--color-border)',
        hoverBorderColor: 'var(--color-border-strong)',
        focusBorderColor: 'var(--color-primary)',
        invalidBorderColor: 'var(--danger)',
        color: 'var(--color-text)',
        placeholderColor: 'var(--color-text-muted)',
        shadow: 'var(--shadow-xs)',
        paddingX: '0.75rem',
        paddingY: '0.5rem',
        borderRadius: 'var(--radius-md)',
        focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'var(--shadow-focus)' },
        transitionDuration: 'var(--duration-fast)',
      },
      css: ({ dt }: StyleOptions) => `
        .p-inputtext {
          border-width: 1px;
          border-style: solid;
          border-color: ${dt('inputtext.border.color') ?? 'var(--p-inputtext-border-color)'};
          border-radius: var(--radius-md);
          font-family: var(--font-ui);
          font-size: 0.9375rem;
          line-height: 1.4;
          min-height: 38px;
          caret-color: var(--color-primary);
          transition: border-color var(--duration-fast) var(--ease-standard),
                      box-shadow var(--duration-fast) var(--ease-standard);
        }
        .p-inputtext:hover { border-color: ${dt('inputtext.hover.border.color') ?? 'var(--p-inputtext-hover-border-color)'}; }
        .p-inputtext:focus,
        .p-inputtext:focus-visible {
          border-color: ${dt('inputtext.focus.border.color') ?? 'var(--p-inputtext-focus-border-color)'};
          box-shadow: var(--shadow-focus);
          outline: none;
        }
        .p-inputtext.ng-invalid.ng-dirty,
        .p-inputtext.p-invalid { border-color: var(--danger); }
        .p-inputtext:-webkit-autofill {
          -webkit-text-fill-color: var(--color-text);
          -webkit-box-shadow: 0 0 0 1000px var(--color-bg-surface) inset;
          transition: background-color 9999s;
        }
      `,
    },
    // Same box as .p-inputtext: 15px / 1.4 text in 8px 12px padding = a 39px control.
    select: {
      root: DROPDOWN_FIELD_ROOT,
      css: () => `
        .p-select-label { font-size: 0.9375rem; line-height: 1.4; }
      `,
    },
    multiselect: {
      root: DROPDOWN_FIELD_ROOT,
      css: () => `
        .p-multiselect-label { font-size: 0.9375rem; line-height: 1.4; }
      `,
    },
    // Input-group affordance icons (the statistics date-range calendar glyph, any
    // search icon). PrimeNG Aura defaults `iconfield.iconColor` to slate-400
    // (#94a3b8) — ~2.6:1 on a white field, below the WCAG 2.1 AA 3:1 floor for a
    // meaningful graphical control (PR #967 feedback). Re-point to the secondary-text
    // token (slate-600, ~7:1 in light, theme-aware in dark) so the glyph is legible.
    iconfield: {
      icon: { color: 'var(--color-text-secondary)' },
    },
    // #3264: an input-group addon carries text (the white-label address prefix
    // "<host>/a/"). Aura colours it with the form-field icon colour, slate-400
    // (2.56:1 on white, fails AA for text), and draws it on the field fill; it takes
    // the secondary-text token, like the iconfield glyph above.
    inputgroup: {
      addon: {
        color: 'var(--color-text-secondary)',
        background: 'var(--color-bg-subtle)',
        borderColor: 'var(--color-border)',
      },
    },
    // #3263 A5 / #3288: an unselected toggle/select-button label (e.g. the public-page
    // locale switcher) is Aura's surface.500 on its surface.100 track, 4.34:1 (below AA
    // for text). It takes the secondary-text token, like the iconfield glyph above. Dark
    // keeps Aura's surface.400 on surface.950. Same block as PR #3314.
    togglebutton: {
      colorScheme: {
        light: {
          root: { color: 'var(--color-text-secondary)' },
          icon: { color: 'var(--color-text-secondary)' },
        },
      },
    },
    // #3510: a segmented control that doesn't fit its card wraps onto more rows
    // instead of running past the border (Target by: +46px on a phone). The track
    // colour moves onto the group and every segment rounds its own corners, so a
    // wrapped control keeps one rounded track; segments that fill a row stretch to
    // its end. On one row nothing changes: fit-content leaves no space to grow into
    // and the same-colour track hides the inner corners.
    selectbutton: {
      css: () => `
        .p-selectbutton {
          flex-wrap: wrap;
          width: fit-content;
          max-width: 100%;
          background: var(--p-togglebutton-background);
        }
        .p-selectbutton .p-togglebutton {
          flex: 1 1 auto;
          border-radius: var(--p-selectbutton-border-radius);
        }
      `,
    },
    datepicker: {
      // Themed date picker (#2531) — replaces native <input type="date">.
      // Panel on surface/border tokens; selected day = primary fill with the
      // theme-aware inverse label (white on navy / slate-900 on navy-300 dark);
      // today = primary-tinted outline so it never reads as "selected" by color alone.
      panel: {
        background: 'var(--color-bg-raised)',
        borderColor: 'var(--color-border)',
        color: 'var(--color-text)',
        borderRadius: 'var(--radius-md)',
        shadow: 'var(--shadow-md)',
      },
      header: {
        background: 'var(--color-bg-raised)',
        borderColor: 'var(--color-border)',
        color: 'var(--color-text)',
      },
      inputIcon: { color: 'var(--color-text-secondary)' },
      // --color-text-muted (slate-400) is ~2.6:1 on the panel; secondary clears AA.
      weekDay: { color: 'var(--color-text-secondary)' },
      date: {
        color: 'var(--color-text)',
        hoverBackground: 'var(--color-bg-subtle)',
        hoverColor: 'var(--color-text)',
        selectedBackground: 'var(--color-primary)',
        selectedColor: 'var(--color-text-inverse)',
        focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'var(--shadow-focus)' },
      },
      buttonbar: { borderColor: 'var(--color-border)' },
      colorScheme: {
        light: { today: { background: 'var(--navy-50)', color: 'var(--color-primary)' } },
        dark: { today: { background: 'var(--color-bg-subtle)', color: 'var(--color-primary)' } },
      },
      css: () => `
        .p-datepicker-today .p-datepicker-day:not(.p-datepicker-day-selected) {
          box-shadow: inset 0 0 0 1px var(--color-primary);
          font-weight: 600;
        }
      `,
    },
    password: {
      icon: { color: 'var(--color-text-muted)' },
      // Selectors verified against primeng@21.x (primeng/fesm2022/primeng-password.mjs).
      css: () => `
        .p-password { display: block; width: 100%; position: relative; }
        .p-password > .p-inputtext { width: 100%; }
        .p-password-toggle-mask-icon {
          right: 0.75rem;
          color: var(--color-text-muted);
          transition: color var(--duration-fast) var(--ease-standard);
        }
        .p-password-toggle-mask-icon:hover { color: var(--color-text); }
      `,
    },
    message: {
      root: { borderRadius: 'var(--radius-md)' },
      colorScheme: {
        light: {
          error: {
            background: 'var(--danger-tint)',
            borderColor: 'color-mix(in srgb, var(--danger) 30%, transparent)',
            color: 'var(--slate-900)',
            shadow: 'none',
          },
          success: {
            background: 'var(--success-tint)',
            borderColor: 'color-mix(in srgb, var(--success) 30%, transparent)',
            color: 'var(--slate-900)',
            shadow: 'none',
          },
          warn: {
            background: 'var(--warning-tint)',
            borderColor: 'color-mix(in srgb, var(--warning) 30%, transparent)',
            color: 'var(--slate-900)',
            shadow: 'none',
          },
          info: {
            background: 'var(--info-tint)',
            borderColor: 'color-mix(in srgb, var(--info) 30%, transparent)',
            color: 'var(--slate-900)',
            shadow: 'none',
          },
        },
      },
    },
    button: {
      root: { borderRadius: 'var(--radius-md)' },
      // No Aura label.fontSize token exists (only label.fontWeight); set via css.
      // #3502: a label is one line. A squeezed button broke "Tilføj kunde" over two
      // lines with its icon floating beside them; the row it sits in wraps instead.
      // On .p-button, not the label span, so a pButton or a.p-button with bare text
      // keeps its line too.
      css: () => `
        .p-button { white-space: nowrap; }
        .p-button-label { font-size: 0.875rem; }
      `,
      colorScheme: {
        light: {
          root: {
            ...BUTTON_FOCUS_RINGS,
            // #3320: Aura's red-500 solid danger fill is 3.76:1 under its white
            // label; --danger is 4.83:1 (the fill app-delete-confirm uses).
            danger: {
              ...BUTTON_FOCUS_RINGS['danger'],
              background: 'var(--danger)',
              borderColor: 'var(--danger)',
              hoverBackground: 'var(--danger-strong)',
              hoverBorderColor: 'var(--danger-strong)',
            },
          },
          // #3265 A5: Aura's red-500 outlined/text danger label is 3.56:1 on the
          // canvas; --danger-strong clears AA on white, canvas and the red-50 hover.
          outlined: { danger: { color: 'var(--danger-strong)' } },
          text: { danger: { color: 'var(--danger-strong)' } },
        },
        dark: {
          // Dark keeps Aura's red-400 danger label (6.45:1 on slate-900).
          root: BUTTON_FOCUS_RINGS,
        },
      },
    },
    radiobutton: {
      // Checked = brand-color dot inside a white circle (not navy-filled).
      // Tokens reference theme-aware CSS vars so dark mode adapts automatically.
      // Default Aura is checkedBackground={primary.color} + white dot; we invert it.
      root: {
        background: 'var(--color-bg-surface)',
        checkedBackground: 'var(--color-bg-surface)',
        checkedHoverBackground: 'var(--color-bg-surface)',
        borderColor: 'var(--color-border)',
        hoverBorderColor: 'var(--color-border-strong)',
        checkedBorderColor: 'var(--color-primary)',
        checkedHoverBorderColor: 'var(--color-primary)',
        checkedFocusBorderColor: 'var(--color-primary)',
        shadow: 'var(--shadow-xs)',
        focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'var(--shadow-focus)' },
      },
      icon: {
        // Inner dot = brand color, sitting on the white background.
        checkedColor: 'var(--color-primary)',
        checkedHoverColor: 'var(--color-primary)',
      },
    },
    menu: {
      // Sidebar nav (§4.6, §7): active = navy left-indicator + navy tint.
      // Selectors verified against primeng@21.x (primeng/fesm2022/primeng-menu.mjs).
      colorScheme: {
        light: {
          root: {
            background: 'transparent',
            borderColor: 'transparent',
            color: 'var(--color-text)',
          },
          item: {
            color: 'var(--color-text-secondary)',
            focusColor: 'var(--color-text)',
            focusBackground: 'var(--slate-100)',
            padding: '0.5rem 0.75rem',
            borderRadius: 'var(--radius-md)',
            icon: {
              color: 'var(--color-text-muted)',
              focusColor: 'var(--color-primary)',
            },
          },
          submenuLabel: {
            color: 'var(--color-text-muted)',
            background: 'transparent',
            padding: '1rem 0.5rem 0.5rem',
            fontWeight: '600',
          },
        },
        dark: {
          root: {
            background: 'transparent',
            borderColor: 'transparent',
            color: 'var(--color-text)',
          },
          item: {
            color: 'var(--color-text-secondary)',
            focusColor: 'var(--color-text)',
            focusBackground: 'var(--slate-800)',
            padding: '0.5rem 0.75rem',
            borderRadius: 'var(--radius-md)',
            icon: {
              color: 'var(--color-text-muted)',
              focusColor: 'var(--color-primary)',
            },
          },
          submenuLabel: {
            color: 'var(--color-text-muted)',
            background: 'transparent',
            padding: '1rem 0.5rem 0.5rem',
            fontWeight: '600',
          },
        },
      },
      css: () => `
        .p-menu .p-menu-submenu-label {
          font-family: var(--font-ui);
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--color-text-muted);
        }
        .p-menu .p-menu-item-link {
          font-family: var(--font-ui);
          font-size: 0.875rem;
          border-radius: var(--radius-md);
          transition: color var(--duration-fast) var(--ease-standard),
                      background var(--duration-fast) var(--ease-standard);
        }
        .p-menu .p-menu-item-link:not(.p-menu-item-link-active):hover {
          color: var(--color-text);
          background: var(--slate-100);
        }
        .p-dark .p-menu .p-menu-item-link:not(.p-menu-item-link-active):hover {
          background: var(--slate-800);
        }
        .p-menu .p-menu-item-link:not(.p-menu-item-link-active):hover .p-menu-item-icon {
          color: var(--color-primary);
        }
        .p-menu .p-menu-item-link.p-menu-item-link-active {
          color: var(--color-primary-active);
          background: var(--navy-50);
          font-weight: 600;
          position: relative;
        }
        .p-dark .p-menu .p-menu-item-link.p-menu-item-link-active {
          color: var(--navy-300);
          background: color-mix(in srgb, var(--navy-500) 16%, transparent);
        }
        .p-menu .p-menu-item-link.p-menu-item-link-active .p-menu-item-label,
        .p-menu .p-menu-item-link.p-menu-item-link-active .p-menu-item-icon {
          color: inherit;
        }
        .p-menu .p-menu-item-link.p-menu-item-link-active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 6px;
          bottom: 6px;
          width: 3px;
          border-radius: var(--radius-full);
          background: var(--color-primary);
        }
      `,
    },
    panelmenu: {
      // Sidebar nav (§4.6, §7): collapsible legacy groups + standalone links.
      // Mirrors the `menu` treatment: active = navy left-indicator + navy tint.
      // Selectors verified against primeng@21.x (primeng/fesm2022/primeng-panelmenu.mjs).
      colorScheme: {
        light: {
          root: { gap: '0.125rem', transitionDuration: 'var(--duration-fast)' },
          panel: {
            background: 'transparent',
            borderColor: 'transparent',
            borderWidth: '0',
            color: 'var(--color-text)',
            padding: '0',
            first: { borderWidth: '0' },
            last: { borderWidth: '0' },
          },
          item: {
            color: 'var(--color-text-secondary)',
            focusColor: 'var(--color-text)',
            focusBackground: 'var(--slate-100)',
            padding: '0.5rem 0.75rem',
            borderRadius: 'var(--radius-md)',
            icon: { color: 'var(--color-text-muted)', focusColor: 'var(--color-primary)' },
          },
          submenuIcon: {
            color: 'var(--color-text-muted)',
            focusColor: 'var(--color-primary)',
          },
        },
        dark: {
          root: { gap: '0.125rem', transitionDuration: 'var(--duration-fast)' },
          panel: {
            background: 'transparent',
            borderColor: 'transparent',
            borderWidth: '0',
            color: 'var(--color-text)',
            padding: '0',
            first: { borderWidth: '0' },
            last: { borderWidth: '0' },
          },
          item: {
            color: 'var(--color-text-secondary)',
            focusColor: 'var(--color-text)',
            focusBackground: 'var(--slate-800)',
            padding: '0.5rem 0.75rem',
            borderRadius: 'var(--radius-md)',
            icon: { color: 'var(--color-text-muted)', focusColor: 'var(--color-primary)' },
          },
          submenuIcon: {
            color: 'var(--color-text-muted)',
            focusColor: 'var(--color-primary)',
          },
        },
      },
      css: () => `
        .p-panelmenu-panel { border: 0; background: transparent; }
        /* group headers + leaf links share the nav-row treatment */
        .p-panelmenu-header-link,
        .p-panelmenu-item-link {
          font-family: var(--font-ui);
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          border-radius: var(--radius-md);
          color: var(--color-text-secondary);
          transition: color var(--duration-fast) var(--ease-standard),
                      background var(--duration-fast) var(--ease-standard);
        }
        /* toggle chevron sits at the right edge of the group header */
        .p-panelmenu-header-link .p-panelmenu-submenu-icon {
          order: 9;
          margin-left: auto;
          color: var(--color-text-muted);
        }
        /* a header with a count badge: the badge takes the free space, the chevron follows it */
        .p-panelmenu-header-link:has(.p-menuitem-badge) .p-panelmenu-submenu-icon {
          margin-left: 0;
        }
        .p-panelmenu-header-icon,
        .p-panelmenu-item-icon { color: var(--color-text-muted); }
        /* #3512 L1: Aura sets the leaf label to line-height 1, so a label that wraps
           ("Facebook audiences and pixels" in Danish runs to 3 lines) had its lines
           touching. Leaf labels take the group-header label's body-sm 1.5. */
        .p-panelmenu-item-label { line-height: 1.5; }
        /* indent leaf links beneath an expanded group */
        .p-panelmenu-submenu { padding-left: 0.75rem; }
        /* PrimeNG's own MenuItem.badge (#3047: the pending sharing-request count on
           "Facebook audiences & pixels" and on its group header) — a count pill pushed to
           the row's right edge. The number itself carries the meaning; the fill only draws
           the eye. The fill/label pair is PrimeNG's own primary colour + contrast colour: they
           flip together in dark mode (light navy with slate-900), where --color-primary and
           --color-on-primary do not (3.03:1). */
        .p-panelmenu-header-link .p-menuitem-badge,
        .p-panelmenu-item-link .p-menuitem-badge {
          margin-left: auto;
          min-width: 1.25rem;
          padding: 0 0.375rem;
          border-radius: var(--radius-full);
          background: var(--p-primary-color, var(--color-primary));
          color: var(--p-primary-contrast-color, var(--color-on-primary));
          font-size: 0.75rem;
          font-weight: 600;
          line-height: 1.25rem;
          text-align: center;
          font-variant-numeric: tabular-nums;
        }

        .p-panelmenu-header-link:not(.p-panelmenu-item-link-active):hover,
        .p-panelmenu-item-link:not(.p-panelmenu-item-link-active):hover {
          color: var(--color-text);
          background: var(--slate-100);
        }
        .p-dark .p-panelmenu-header-link:not(.p-panelmenu-item-link-active):hover,
        .p-dark .p-panelmenu-item-link:not(.p-panelmenu-item-link-active):hover {
          background: var(--slate-800);
        }
        .p-panelmenu-header-link:not(.p-panelmenu-item-link-active):hover .p-panelmenu-header-icon,
        .p-panelmenu-item-link:not(.p-panelmenu-item-link-active):hover .p-panelmenu-item-icon {
          color: var(--color-primary);
        }

        /* #3264 A2 (§9): keyboard focus shows the shadow-focus ring. Aura only
           tints a focused header (and plain links got nothing), so a keyboard
           user could not see where they were in the nav. A header takes focus on
           its div[role=button] and paints its content box; a leaf link is the
           focused element itself. */
        .p-panelmenu-header:not(.p-disabled):focus-visible .p-panelmenu-header-content,
        .p-panelmenu-item-link:focus-visible {
          outline: 0;
          box-shadow: var(--shadow-focus);
        }

        /* active (routerLinkActive) — applied to both standalone header links
           and leaf links: navy left indicator + tint */
        .p-panelmenu-item-link-active {
          color: var(--color-primary-active);
          background: var(--navy-50);
          font-weight: 600;
          position: relative;
        }
        .p-dark .p-panelmenu-item-link-active {
          color: var(--navy-300);
          background: color-mix(in srgb, var(--navy-500) 16%, transparent);
        }
        .p-panelmenu-item-link-active .p-panelmenu-header-label,
        .p-panelmenu-item-link-active .p-panelmenu-item-label,
        .p-panelmenu-item-link-active .p-panelmenu-header-icon,
        .p-panelmenu-item-link-active .p-panelmenu-item-icon {
          color: inherit;
        }
        .p-panelmenu-item-link-active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 6px;
          bottom: 6px;
          width: 3px;
          border-radius: var(--radius-full);
          background: var(--color-primary);
        }
      `,
    },
    toolbar: {
      // Topbar (§4.6): 64px, surface-0, 1px bottom border.
      // Selectors verified against primeng@21.x (primeng/fesm2022/primeng-toolbar.mjs).
      root: { borderRadius: '0', padding: '0 2.25rem' },
      colorScheme: {
        light: {
          root: {
            background: 'var(--color-bg-surface)',
            borderColor: 'var(--color-border)',
            color: 'var(--color-text)',
          },
        },
        dark: {
          root: {
            background: 'var(--color-bg-surface)',
            borderColor: 'var(--color-border)',
            color: 'var(--color-text)',
          },
        },
      },
    },
  },
})
